
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// module components
import PermissionsListItem from '@/modules/GatewayModule/components/PermissionsListItem/index.vue'
import PermissionsActions from '@/modules/GatewayModule/components/PermissionsActions/index.vue'

// types
import {
    CreatePermissionPayloadT,
    GatewayMicroserviceT,
    GatewayPermissionT,
} from '@/core/types/gateway.types'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// composable
import useActions from '@/common/composable/useActions'
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-input': TheInput,
        'the-button': TheButton,
        'the-select': TheSelect,
        'permissions-list-item': PermissionsListItem,
        'permissions-actions': PermissionsActions,
    },
    setup() {
        const notifications = useNotifications()

        const data_state = reactive({
            permissions: new DataState<GatewayPermissionT>({}),
            microservices: new DataState<GatewayMicroserviceT>({}),
        })

        const filters_state = reactive({
            name: undefined as string | undefined,
            serviceId: undefined as string | undefined,
        })

        const loadPermissions = async (page: number) => {
            data_state.permissions.pagination.current_page = page

            try {
                data_state.permissions.is_loading = true

                const response = await gateway.getPermissions(
                    page,
                    filters_state.name,
                    filters_state.serviceId
                )
                data_state.permissions.list = response.data
                data_state.permissions.pagination.total_pages = response.meta.last_page

                data_state.permissions.is_failed = false
                data_state.permissions.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.permissions.failure.catchError(error)

                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_failed = true
                }
            } finally {
                if (!data_state.permissions.failure.error_state.is_cancel) {
                    data_state.permissions.is_loading = false
                }
            }
        }

        // microservices

        const loadMicroservices = async (page: number, search?: string) => {
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true
                const response = await gateway.getMicroservices(page, search)
                data_state.microservices.list = response.data

                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const loadMoreMicroservices = async (page: number) => {
            if (page > data_state.microservices.pagination.total_pages) return
            data_state.microservices.pagination.current_page = page

            try {
                data_state.microservices.is_loading = true

                const response = await gateway.getMicroservices(page)
                data_state.microservices.list = data_state.microservices.list.concat(response.data)

                data_state.microservices.pagination.total_pages = response.meta.last_page

                data_state.microservices.is_failed = false
                data_state.microservices.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.microservices.failure.catchError(error)

                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_failed = true
                }
            } finally {
                if (!data_state.microservices.failure.error_state.is_cancel) {
                    data_state.microservices.is_loading = false
                }
            }
        }

        const searchMicroservices = (search_query: string) => {
            loadMicroservices(1, search_query)
        }

        const unshiftFounddMicroservices = async (microservice: GatewayMicroserviceT) => {
            await loadMicroservices(data_state.microservices.pagination.current_page)
            data_state.microservices.list.unshift(microservice)
        }

        onBeforeMount(() => {
            loadPermissions(1)
            loadMicroservices(1)
        })

        const { actions_state, startCreate, startUpdate } = useActions<GatewayPermissionT>()

        const createPermission = async (permission: CreatePermissionPayloadT) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createPermission(permission)
                await loadPermissions(data_state.permissions.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updatePermission = async (permission: CreatePermissionPayloadT & { id: number }) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updatePermission(permission.id, permission)
                await loadPermissions(data_state.permissions.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const deletePermission = async (id: number) => {
            const answer = confirm(`Вы действительно хотите удалить доступ (ID: ${id})?`)
            if (!answer) return

            try {
                actions_state.is_actions_loading = true
                await gateway.deletePermission(id)
                await loadPermissions(data_state.permissions.pagination.current_page)
                actions_state.is_actions_opened = false
                notifications.notify({
                    title: `Доступ (ID: ${id}) успешно удален!`,
                    type: 'success',
                })
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const resetFilters = () => {
            filters_state.name = undefined
            filters_state.serviceId = undefined

            loadPermissions(1)
        }

        return {
            data_state,
            filters_state,
            loadPermissions,
            actions_state,
            startCreate,
            startUpdate,
            deletePermission,
            createPermission,
            updatePermission,
            resetFilters,
            // microservices
            loadMicroservices,
            loadMoreMicroservices,
            searchMicroservices,
            unshiftFounddMicroservices,
        }
    },
})
